import {
  JobsListResponseSchema,
  JobRetryResponseSchema,
  type JobsListResponse,
  type JobRetryResponse,
  type JobStatus,
} from '../../src/shared/jobs';
import { apiGet, apiPost } from './client';

/**
 * Typed client for the workspace jobs surface (ARCHITECTURE_PLAN §9):
 *   GET  /api/w/:slug/jobs?status   — list jobs, optional status filter
 *   POST /api/w/:slug/jobs/:id/retry — reset a failed/dead job to queued
 *
 * Each endpoint validates the response against the shared zod schemas
 * before returning it, so a contract drift fails loudly (§3).
 */

/** React Query key prefix for jobs data. */
export const jobsKeys = {
  all: (slug: string) => ['jobs', slug] as const,
  list: (slug: string, status?: JobStatus) => ['jobs', slug, 'list', status ?? 'all'] as const,
};

/**
 * `GET /api/w/:slug/jobs` — list background jobs for the workspace.
 * @param slug  workspace slug
 * @param status  optional status filter; omit for all
 * @param signal  AbortSignal for query cancellation
 */
export function fetchJobs(
  slug: string,
  status?: JobStatus,
  signal?: AbortSignal
): Promise<JobsListResponse> {
  const params = status !== undefined ? `?status=${status}` : '';
  return apiGet(`/api/w/${slug}/jobs${params}`, JobsListResponseSchema, signal);
}

/**
 * `POST /api/w/:slug/jobs/:id/retry` — retry a failed or dead job.
 * The server resets status to `queued` and re-enqueues it.
 */
export function retryJob(slug: string, jobId: string): Promise<JobRetryResponse> {
  return apiPost(`/api/w/${slug}/jobs/${jobId}/retry`, {}, JobRetryResponseSchema);
}
